<script setup>
import BaseIcon from '@/components/foundations/BaseIcon.vue'
import { useAttrs } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    validator: (v) => ['primary', 'secondary', 'tertriary', 'link', 'linkSubtle', 'accent', 'filtermain', 'filterchips', 'filterQuick', 'multiselect', 'pagination', 'counterOffer'].includes(v),
  },
  size: {
    type: String,
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  icon: String,
  iconPosition: {
    type: String,
    validator: (v) => ['left', 'right'].includes(v),
  },
  loading: Boolean,
})

const $attrs = useAttrs()

const sizeClasses = {
  xs: 'h-[30px] px-[16px] text-[14px]',
  sm: 'h-[36px] px-[16px] text-[16px]',
  md: 'h-[42px] px-[16px] text-[16px]',
  lg: 'h-[48px] px-[16px] text-[16px]',
}

const variantClasses = {
  primary: 'text-white bg-[#0047D6] enabled:hover:bg-[#0C37B8] enabled:active:bg-[#0D1A3F]',
  secondary: 'text-[#2d64dc] bg-white border border-solid border-[#0047D6] enabled:hover:text-white enabled:hover:bg-[#0C37B8] enabled:active:text-white enabled:active:bg-[#0D1A3F]',
  tertriary: 'text-[#131415] bg-[#EBEEF5] enabled:hover:text-[#2254c5] enabled:active:text-[#0047D6] enabled:active:bg-[#689AF8]',
  link: 'text-[#0047D6] bg-white enabled:hover:text-[#2254c5] enabled:hover:bg-[#EBEEF5] enabled:active:text-[#fff] enabled:active:bg-[#0D1A3F]',
  linkSubtle: 'text-[#0047D6] bg-none enabled:hover:text-[#2254c5] enabled:active:text-[#2254c5] h-[20px] px-0',
  accent: 'text-white bg-[#0D1A3F] enabled:hover:bg-[#0D1A3F] enabled:active:bg-[#0D1A3F]',
  filtermain: 'text-[#131415] bg-white border border-solid border-[#DCDFE8] enabled:hover:bg-[#EBEEF5] enabled:hover:border-[#6D737A] enabled:active:bg-[#EBEEF5] enabled:active:border-[#6D737A]',
  filterchips: 'text-[#0047D6] bg-[#EBEEF5] enabled:hover:bg-[#DCDFE8] enabled:active:bg-[#DCDFE8]',
  filterQuick: 'text-[#131415] rounded-full border border-solid border-black enabled:hover:bg-white enabled:hover:border-[#6D737A] active:bg-white enabled:active:border-[#6D737A]',
  counterOffer: 'text-[#131415] rounded-full border border-solid border-black enabled:hover:bg-black enabled:hover:text-white enabled:hover:border-[#6D737A] active:bg-black active:text-white enabled:active:border-[#6D737A]',
  multiselect: 'bg-[var(--primary-low)] text-[var(--text-cta)] h-[26px] min-h-0 min-w-auto text-[12px] leading-[16px] py-[6px] px-[4px] font-medium gap-[4px]',
  pagination: 'rounded-full p-0 min-w-0 min-h-0'
}
</script>

<template>
  <button
    :class="[
      'relative select-none rounded-[4px] inline-flex items-center justify-center font-[590] whitespace-nowrap gap-[4px]',
      variant !== 'linkSubtle' ? sizeClasses[size || 'md'] : '',
      variantClasses[variant || 'primary'],
      { 'disabled:text-[#8790a0] disabled:bg-[#f1f2f4] disabled:border-[#f1f2f4]': variant !== 'linkSubtle' && !loading },
      { 'disabled:text-[#8790a0] disabled:bg-transparent': variant === 'linkSubtle' && !loading }
    ]"
    v-bind="$attrs"
    :disabled="loading || !!$attrs.disabled"
    :aria-busy="loading || undefined"
  >
    <div v-if="icon && (iconPosition || 'left') === 'left'" class="w-[20px] h-[20px] flex items-center" :class="{ 'opacity-0': loading }">
      <BaseIcon :name="icon" class="w-[20px] h-[20px] m-auto" />
    </div>
    <span v-if="$slots.default" :class="{ 'opacity-0': loading }"><slot /></span>
    <div v-if="icon && iconPosition === 'right'" class="w-[20px] h-[20px] flex items-center" :class="{ 'opacity-0': loading }">
      <BaseIcon :name="icon" class="w-[20px] h-[20px] m-auto" />
    </div>
    <span
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center pointer-events-none"
      aria-hidden="true"
    >
      <span class="w-[20px] h-[20px] border-[2px] border-current border-t-transparent rounded-full animate-spin" />
    </span>
  </button>
</template>
